/**
 * Cryptographic utility functions for secure operations
 */

/**
 * Generates an HMAC-SHA256 signature
 * @param clientSecret - The secret key for HMAC
 * @param clientTimestamp - The timestamp in seconds
 * @returns Base64-encoded HMAC signature
 */
export const generateHmacSignature = async (clientSecret: string, clientTimestamp: number): Promise<string> => {
  const message = `nonce:${clientTimestamp}`;

  // Convert the secret to a CryptoKey
  const encoder = new TextEncoder();
  const keyData = encoder.encode(clientSecret);
  const key = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);

  // Generate the HMAC
  const messageData = encoder.encode(message);
  const signature = await crypto.subtle.sign('HMAC', key, messageData);

  // Convert to Base64
  const signatureArray = Array.from(new Uint8Array(signature));
  const signatureBase64 = btoa(String.fromCharCode(...signatureArray));

  return signatureBase64;
};

/**
 * Generates a UUID v4 string without dashes
 * @returns UUID string without dashes
 */
export const generateUuidWithoutDashes = (): string => {
  return crypto.randomUUID().replace(/-/g, '');
};
